/**
 * Formatting utilities that accept currency/unit from data.
 *
 * Key principle: Symbol and unit come from the data being displayed,
 * not from user preferences. This allows showing £/m² for London
 * and $/sf for NYC on the same page.
 *
 * Browser locale (from window.I18n.numberFormat.locale) is used only
 * for number formatting (1,000.00 vs 1.000,00).
 */

// Get locale from server-provided I18n config
function getLocale() {
  return window.I18n?.numberFormat?.locale || "en"
}

const Formatters = {
  /**
   * Format a number as currency.
   * @param {number} value - The value to format
   * @param {object} opts - Options
   * @param {string} opts.symbol - Currency symbol (default: "$")
   * @param {number} opts.decimals - Decimal places (default: 2)
   * @param {boolean} opts.compact - Use compact notation (default: false)
   * @param {boolean} opts.accounting - Use accounting format for negatives (default: false)
   * @returns {string} Formatted currency string
   *
   * @example
   * Formatters.currency(1500, { symbol: "$" }) // "$1,500.00"
   * Formatters.currency(1500, { symbol: "£" }) // "£1,500.00"
   * Formatters.currency(1500000, { symbol: "$", compact: true }) // "$1.5M"
   * Formatters.currency(-500, { symbol: "$", accounting: true }) // "($500.00)"
   */
  currency(value, opts = {}) {
    if (!opts.symbol) {
      throw new Error(
        "Currency symbol is required. Currency symbols must be passed from backend based on deal/account currency. " +
          "Silently defaulting to USD would risk displaying incorrect currency symbols and causing financial errors.",
      )
    }
    const symbol = opts.symbol
    if (value == null) {
      const decimals = opts.decimals ?? 2
      return decimals > 0 ? `${symbol}0.${"0".repeat(decimals)}` : `${symbol}0`
    }

    const num = parseFloat(value)
    const locale = getLocale()

    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: opts.decimals ?? 2,
      maximumFractionDigits: opts.decimals ?? 2,
      notation: opts.compact ? "compact" : "standard",
    })

    const formatted = formatter.format(Math.abs(num))

    if (opts.accounting && num < 0) {
      return `(${symbol}${formatted})`
    }

    return num < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`
  },

  /**
   * Format a number with locale-appropriate separators.
   * @param {number} value - The value to format
   * @param {object} opts - Options
   * @param {number} opts.decimals - Decimal places (default: 0)
   * @param {boolean} opts.compact - Use compact notation (default: false)
   * @returns {string} Formatted number string
   *
   * @example
   * Formatters.number(1500000) // "1,500,000"
   * Formatters.number(1500000, { compact: true }) // "1.5M"
   */
  number(value, opts = {}) {
    if (value == null) return "0"

    const locale = getLocale()

    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: opts.decimals ?? 0,
      maximumFractionDigits: opts.decimals ?? 0,
      notation: opts.compact ? "compact" : "standard",
      trailingZeroDisplay: "stripIfInteger",
    })

    return formatter.format(value)
  },

  /**
   * Format an area value with unit.
   * @param {number} value - The area value
   * @param {object} opts - Options
   * @param {string} opts.unit - Area unit (default: "sf")
   * @param {boolean} opts.compact - Use compact notation (default: false)
   * @returns {string} Formatted area string
   *
   * @example
   * Formatters.area(10000, { unit: "sf" }) // "10,000 sf"
   * Formatters.area(929, { unit: "m²" }) // "929 m²"
   * Formatters.area(1500000, { unit: "sf", compact: true }) // "1.5M sf"
   */
  area(value, opts = {}) {
    const unit = opts.unit || ""
    if (value == null) return `0 ${unit}`

    const formatted = this.number(value, opts)
    return `${formatted} ${unit}`
  },

  /**
   * Format a price per unit value.
   * @param {number} value - The price value
   * @param {object} opts - Options
   * @param {string} opts.symbol - Currency symbol (default: "$")
   * @param {string} opts.unit - Area unit (default: "sf")
   * @param {string} opts.period - Optional period suffix (e.g., "yr")
   * @param {number} opts.decimals - Decimal places (default: 2)
   * @returns {string} Formatted price per unit string
   *
   * @example
   * Formatters.pricePerUnit(45.50, { symbol: "$", unit: "sf" }) // "$45.50/sf"
   * Formatters.pricePerUnit(45.50, { symbol: "£", unit: "m²" }) // "£45.50/m²"
   * Formatters.pricePerUnit(45.50, { symbol: "$", unit: "sf", period: "yr" }) // "$45.50/sf/yr"
   */
  pricePerUnit(value, opts = {}) {
    const symbol = opts.symbol || ""
    const unit = opts.unit || ""

    if (value == null) return `${symbol}0/${unit}`

    const formatted = this.currency(value, { symbol, decimals: opts.decimals })
    const period = opts.period ? `/${opts.period}` : ""

    return `${formatted}/${unit}${period}`
  },

  /**
   * Create a currency formatter function with pre-bound symbol.
   * Useful for chart callbacks that need a simple function.
   * @param {string} symbol - Currency symbol
   * @param {object} opts - Additional options passed to currency()
   * @returns {function} Formatter function
   *
   * @example
   * const fmt = Formatters.currencyFormatter("$", { accounting: true })
   * fmt(-500) // "($500.00)"
   */
  currencyFormatter(symbol, opts = {}) {
    return (value) => this.currency(value, { symbol, ...opts })
  },

  /**
   * Create an area formatter function with pre-bound unit.
   * @param {string} unit - Area unit
   * @param {object} opts - Additional options passed to area()
   * @returns {function} Formatter function
   */
  areaFormatter(unit, opts = {}) {
    return (value) => this.area(value, { unit, ...opts })
  },
}

export default Formatters
